import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import {
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { MatSliderModule } from '@angular/material/slider';
import {
  catchError,
  filter,
  map,
  merge,
  shareReplay,
  take,
  takeUntil,
  tap,
  throwError,
  timer,
} from 'rxjs';
import { BaseDialog } from '../../../../components/dialog/base-dialog';
import { ConfirmHandler } from '../../../../components/dialog/dialog.model';

@Component({
  selector: 'app-signaling-block-image-config-dialog',
  imports: [
    MatButton,
    MatDialogActions,
    MatDialogClose,
    MatDialogContent,
    MatDialogTitle,
    MatRadioButton,
    MatRadioGroup,
    MatProgressSpinner,
    MatSliderModule,
    ReactiveFormsModule,
  ],
  templateUrl: './signaling-block-image-config-dialog.component.html',
  styleUrl: './signaling-block-image-config-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SignalingBlockImageConfigDialogComponent
  extends BaseDialog<{
    type: 'random' | 'slideshow';
    slideDuration: number;
    onConfirm: ConfirmHandler;
  }>
  implements OnInit
{
  private readonly _fb = inject(FormBuilder);
  private readonly _dialogRef =
    inject<MatDialogRef<SignalingBlockImageConfigDialogComponent>>(MatDialogRef);

  protected readonly form = this._fb.group({
    type: ['random'],
    slideDuration: [10],
  });

  protected readonly sliderOptions = {
    min: 10,
    max: 60,
    step: 5,
  };

  protected readonly options = [
    {
      value: 'random',
      displayValue: 'Willekeurig',
      description: 'Afbeelding wordt willekeurig gekozen en getoond',
    },
    {
      value: 'slideshow',
      displayValue: 'Slideshow',
      description: 'Afbeeldingen worden getoond in volgorde',
    },
  ];

  protected readonly submitted = signal(false);
  protected readonly loading = signal(false);

  get controls() {
    return this.form.controls;
  }

  constructor() {
    super();
  }

  ngOnInit() {
    if (this.data) {
      this.form.patchValue(this.data);
    }
  }

  override handleConfirm(): void {
    this.submitted.set(true);
    this.form.disable();

    const callback$ = this.data.onConfirm(this.form.value);
    const source$ = callback$.pipe(
      shareReplay(1),
      catchError((error) => {
        this.submitted.set(false);
        this.form.disable();

        return throwError(() => error);
      })
    );

    const loading$ = merge(
      timer(1000).pipe(
        map(() => true),
        takeUntil(source$)
      ),

      source$.pipe(map(() => false))
    );

    loading$
      .pipe(
        tap((state) => this.loading.set(state)),
        filter((state) => !state),
        tap(() => this._dialogRef.close(true)),
        take(1)
      )
      .subscribe();
  }
}
